import React from "react";
import { create } from "zustand";
import { gql } from "@apollo/client";
import client, { hasAppSyncTrustedCognitoSession } from "../../apollo/client";
import ModalStore from "../../components/Modal/ModalStore";
import { LIST_CART_BY_USERID } from "../../apollo/queries/cart";
import { SADDLE_CHECKOUT } from "../../apollo/mutations/saddle";
import { CREATE_CART, DELETE_CART } from "../../apollo/mutations/cart";
import { Modal } from "stores/modal/useModalStore";
import SeatSizeSelectInModal from "pages/BuySaddles/components/SeatSizeSelectInModal";
import { PORoutingTag } from "../../API";

declare global {
  interface Window {
    klaviyo?: any;
  }
}

/** MAIN uses platform Stripe only; SUBDOMAIN expects manufacturer Stripe Connect when Shopify config exists. */
function checkoutDomainTypeFromHost(hostname: string): "MAIN" | "SUBDOMAIN" {
  if (
    hostname === "saddlefit.io" ||
    hostname === "staging.saddlefit.io" ||
    hostname === "develop.saddlefit.io"
  ) {
    return "MAIN";
  }
  if (/^localhost(:\d+)?$/i.test(hostname) || /^127\.0\.0\.1(:\d+)?$/.test(hostname)) {
    return "MAIN";
  }
  return "SUBDOMAIN";
}

/** Optional checkout override: set `VITE_PO_ROUTING_TAG=BILLY_COOK_ORDER` or `SADDLEFIT_ORDER` in `.env`. */
function poRoutingTagFromEnv(): PORoutingTag | undefined {
  const raw = import.meta.env.VITE_PO_ROUTING_TAG;
  if (raw === PORoutingTag.BILLY_COOK_ORDER || raw === PORoutingTag.SADDLEFIT_ORDER) {
    return raw;
  }
  return undefined;
}

interface CartStore {
  cartData: any[];
  loadingState: {
    fetchCartLoading: boolean;
    addToCartLoading: boolean | null;
    removeCartLoading: boolean;
  };
  userCartData: any;
  price: number;
  loadingItemId: string | null;
  addSaddleFitReportTax: boolean;
  selectCartItem: any[];
  selectedVariant: any[];
  isCartRemoved: boolean;
  selectedSeatSize: string | null;
  selectedColor: string | null;
  setPrice: (price: number) => void;
  setCartData: (data: any[]) => void;
  fetchCartData: () => void;
  removeFromCart: (cartItemId: string) => void;
  setSelectCartItem: (cartItem: any) => void;
  setIsCartRemoved: (value: boolean) => void;
  handleRemove: (id: string) => void;
  handlePrice: () => void;
  proceedBtnHandler: () => Promise<void>;
  removeSelectCartItemHandler: () => Promise<void>;
  setSaddleFitReportPrice: () => void;
  setSelectedSeatSize: (size: string | null) => void;
  setSelectedColor: (color: string | null) => void;
  setSelectedVariant: (variant: any[]) => void;
}

export const useCartStore = create<CartStore>((set, get) => ({
  cartData: [],
  userCartData: null,
  price: 0,
  loadingItemId: null,
  addSaddleFitReportTax: false,
  selectCartItem: [],
  isCartRemoved: false,
  selectedSeatSize: null,
  selectedColor: null,
  selectedVariant: null,
  loadingState: {
    fetchCartLoading: false,
    addToCartLoading: null,
    removeCartLoading: false,
    checkoutLoading: false,
  },
  setSelectedVariant: (variant) => set({ selectedVariant: variant }),
  setPrice: (price) => set({ price }),
  setCartData: (cartData) => set({ cartData }),
  setSelectedSeatSize: (size) => set({ selectedSeatSize: size }),
  setSelectedColor: (color) => set({ selectedColor: color }),
  fetchCartData: async () => {
    set((state) => ({ loadingState: { ...state.loadingState, fetchCartLoading: true } }));
    try {
      const canUseCognito = await hasAppSyncTrustedCognitoSession();
      if (!canUseCognito) {
        set((state) => ({
          userCartData: null,
          cartData: [],
          loadingState: { ...state.loadingState, fetchCartLoading: false },
        }));
        return;
      }

      const { data, error } = await client.query({
        query: gql(LIST_CART_BY_USERID),
        variables: { limit: 100, nextToken: null },
        fetchPolicy: "network-only",
      });

      if (error) {
        console.error("Error fetching cart data:", error);
        set((state) => ({ loadingState: { ...state.loadingState, fetchCartLoading: false } }));
        return;
      }

      set((state) => ({
        userCartData: data?.listCartByUserId,
        cartData: data?.listCartByUserId?.items || [],
        loadingState: { ...state.loadingState, fetchCartLoading: false }
      }));
    } catch (err) {
      console.error("Failed to fetch cart data", err);
      set((state) => ({ loadingState: { ...state.loadingState, fetchCartLoading: false } }));
    }
  },

  removeFromCart: async (cartItemId) => {
    set((state) => ({ loadingState: { ...state.loadingState, removeCartLoading: true }, loadingItemId: cartItemId }));
    try {
      const { errors } = await client.mutate({
        mutation: gql(DELETE_CART),
        variables: {
          input: { saddleIds: cartItemId },
        },
        refetchQueries: ['listCartByUserId'],
      });

      if (errors) {
        console.error("Error removing from cart:", errors);
        set((state) => ({ loadingState: { ...state.loadingState, removeCartLoading: false } }));
        return;
      }

      set((state) => {
        const filteredCartData = state.cartData.filter((item) => {
          return (item.id || item.saddleId || item.productData?.sk) !== cartItemId;
        });
        let filteredUserCartData = state.userCartData;
        if (filteredUserCartData && Array.isArray(filteredUserCartData.items)) {
          filteredUserCartData = {
            ...filteredUserCartData,
            items: filteredUserCartData.items.filter((item) => (item.id || item.saddleId || item.productData?.sk) !== cartItemId)
          };
        }
        return {
          userCartData: filteredUserCartData,
          cartData: filteredCartData,
          loadingState: { ...state.loadingState, removeCartLoading: false },
        };
      });
    } catch (err) {
      console.error("Failed to remove from cart", err);
      set((state) => ({ loadingState: { ...state.loadingState, removeCartLoading: false }, loadingItemId: null }));
    } finally {
      set(() => ({ loadingItemId: null }))
    }
  },

  handleRemove: (id) => {
    set({ loadingItemId: id });
    try {
      get().removeFromCart(id);
      set((state) => ({ isCartRemoved: !state.isCartRemoved }));
      get().handlePrice();
    } catch (error) {
      console.error("Error removing item:", error?.message);
    }
  },

  handleAddToCart: async (item, selectedHorse, changeActionState, open, score, fromVariantPicker = false) => {
    set({ loadingState: { ...get().loadingState, addToCartLoading: item?.sk }});

    if (item) {
      const needsVariantPicker =
        !fromVariantPicker &&
        (item?.type === "PRODUCT" ||
          (item?.type === "VARIANT" && item?.brandName === "billycook"));

      if (needsVariantPicker) {
        ModalStore.openSimpleSuccess({
          title: "Choose Your Seat Size",
          subtitle: "Select seat size and color before adding to cart",
          shouldCloseOnOverlayClick: false,
          showHeader: true,
          subTitleFont: "16px",
          children: React.createElement(SeatSizeSelectInModal, {
            item,
            selectedHorse
          }),
        });

        set({ loadingState: { ...get().loadingState, addToCartLoading: null } });
        return;
      }

      set({ loadingItemId: item?.sk });

      try {
        const existingItems = get().userCartData?.items ?? [];
        const index = existingItems.findIndex(
          (elem) =>
            elem?.productData?.sk === item?.sk || elem?.saddleId === item?.sk
        );

        if (index !== -1) {
          set({ loadingItemId: null });
          changeActionState({ adding: false });
          open(Modal.ShoppingCart);
        } else {
          const cartItemPayload = {
            saddleId: item?.sk,
            horseId: selectedHorse?.value,
            score: item?.score ?? score,
            brandName: item?.brandName,
          };

          const { data, errors } = await client.mutate({
            mutation: gql(CREATE_CART),
            variables: { input: cartItemPayload },
            refetchQueries: ["listCartByUserId"],
          });

          if (errors?.length) {
            console.error("Error adding to cart:", errors);
            set({ loadingItemId: null });
            ModalStore.openSimpleError({
              title: "Error",
              subtitle: errors[0]?.message || "Could not add to cart",
              shouldCloseOnOverlayClick: false,
              showHeader: true,
              children: undefined,
            });
            return;
          }

          const created = data?.createCart;
          if (!created) {
            throw new Error("Could not add to cart");
          }

          set((state) => ({
            cartData: [...(state.cartData ?? []), created],
            userCartData: {
              ...(state.userCartData ?? {}),
              items: [...(state.userCartData?.items ?? []), created],
            },
          }));

          if (window.klaviyo) {
            window.klaviyo.push(["track", "Added to Cart", item]);
          }
          get().handlePrice();
          set({ loadingItemId: null });
          changeActionState({ adding: false });
          open(Modal.ShoppingCart);
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        set({
          loadingItemId: null,
          loadingState: { ...get().loadingState, addToCartLoading: null },
        });
        ModalStore.closeAll();
        ModalStore.openSimpleError({
          title: "Error",
          subtitle: error?.message || "Could not add to cart",
          shouldCloseOnOverlayClick: false,
          showHeader: true,
          children: undefined,
        });
        return;
      } finally {
        set({
          loadingState: { ...get().loadingState, addToCartLoading: null },
          selectedVariant: null,
          selectedSeatSize: null,
          selectedColor: null,
        });
        ModalStore.closeAll();
      }
    }
  },

  handlePrice: () => {
    const totalPrice =
      get().userCartData?.items.length > 0 &&
      get().userCartData?.items?.reduce((accumulator, currentValue) => {
        if (currentValue?.hasSaddleFitReport) {
          return accumulator + currentValue?.productData?.price + 100;
        } else {
          return accumulator + currentValue?.productData?.price;
        }
      }, 0);
    set({ price: totalPrice });
  },

  proceedBtnHandler: async () => {
    set((state) => ({ loadingState: { ...state.loadingState, checkoutLoading: true } }));
    const userCartData = get().userCartData;
    if (!userCartData?.items?.length) {
      ModalStore.openSimpleError({
        subtitle: "Please Add To Cart First.",
        shouldCloseOnOverlayClick: false,
        showHeader: true,
        children: undefined,
      });
      set((state) => ({ loadingState: { ...state.loadingState, checkoutLoading: false } }));
      return;
    }
    if (userCartData?.items?.length > 0) {
      const itemIds = userCartData.items.map((data) => data?.productData?.sk);
      const brandName = Array.from(new Set(userCartData.items.map((data) => data?.brandName || data?.productData?.brandName)))[0];
      
      const hostname = window.location.host;
      const domainType = checkoutDomainTypeFromHost(hostname);
      const poRoutingTag = poRoutingTagFromEnv();

      try {
        if (window.klaviyo) {
          window.klaviyo.push(["track", "Started Checkout"]);
        }
        const response = await client.mutate({
          mutation: gql(SADDLE_CHECKOUT),
          variables: {
            input: {
              saddleIds: itemIds,
              brand: brandName,
              domainType: domainType,
              returnOrigin: window.location.origin,
              ...(poRoutingTag ? { poRoutingTag } : {}),
            },
          },
        });
        const sessionURL = response.data.saddleCheckout.sessionURL;
        if (sessionURL) {
          await get().removeSelectCartItemHandler();
          window.location.href = sessionURL;
        }
      } catch (error) {
        console.error("Mutation error:", error.message);
        ModalStore.openSimpleError({
          title: "Error",
          subtitle: error?.message,
          shouldCloseOnOverlayClick: false,
          showHeader: true,
          children: undefined,
        });
      } finally {
        set((state) => ({ loadingState: { ...state.loadingState, checkoutLoading: false } }));
      }
    }
  },

  removeSelectCartItemHandler: async () => {
    const selectCartItem = get().selectCartItem;
    if (selectCartItem.length > 0) {
      get().removeFromCart(selectCartItem[0]);
      set((state) => ({
        isCartRemoved: !state.isCartRemoved,
        selectCartItem: [],
      }));
    }
  },

  setSaddleFitReportPrice: () => {
    set({ addSaddleFitReportTax: true });
  },

  setSelectCartItem: (cartItem) => set({ selectCartItem: cartItem }),
  setIsCartRemoved: (value) => set({ isCartRemoved: value }),
}));
